import { monthRange } from "./shared";
import { fetchDeliveryNotesLive } from "./deliveryNotes";

const toAmount = (value) => Number(value) || 0;

export const dnMarketPrice = (item) =>
  toAmount(item?.marketPrice ?? item?.marketprice);

export const dnSpentAmount = (item) =>
  toAmount(
    item?.saleNetAmount ??
      item?.salenetamount ??
      item?.saleTotalAmount ??
      item?.amount
  );

export const dnSavedAmount = (item) => {
  const marketPrice = dnMarketPrice(item);
  const spent = dnSpentAmount(item);
  if (!(marketPrice > 0)) return 0;
  return Math.max(0, marketPrice - spent);
};

const savingsPercent = (saved, marketPrice) =>
  marketPrice > 0 ? (saved / marketPrice) * 100 : 0;

export const summarizeSavings = (rows = []) => {
  const totalMarketPrice = rows.reduce(
    (sum, item) => sum + dnMarketPrice(item),
    0
  );
  const totalSpent = rows.reduce((sum, item) => sum + dnSpentAmount(item), 0);
  const totalSaved = rows.reduce((sum, item) => sum + dnSavedAmount(item), 0);

  return {
    totalDeliveryNotes: rows.length,
    totalMarketPrice,
    totalSpent,
    totalSaved,
    savingsPercent: savingsPercent(totalSaved, totalMarketPrice),
  };
};

export const mapSavingsRow = (item) => {
  const marketPrice = dnMarketPrice(item);
  const spent = dnSpentAmount(item);
  const saved = dnSavedAmount(item);
  return {
    id: item.id,
    deliveryNoteNumber: item.deliveryNoteNumber || item.id,
    plateNumber: item.plateNumber || "",
    vehicleName: item.vehicleName || item.label || "",
    serviceDate: item.serviceDate || item.createdTime,
    marketPrice,
    spent,
    saved,
    savingsPercent: savingsPercent(saved, marketPrice),
  };
};

const fetchSavingsNotes = ({ from, to } = {}) =>
  fetchDeliveryNotesLive({
    from,
    to,
    invoiceMade: [1, 0],
    limit: 1000,
    maxPages: 1,
  });

const groupSavingsByVehicle = (rows = []) => {
  const grouped = new Map();
  rows.forEach((item) => {
    const plate = item.plateNumber || "—";
    const current = grouped.get(plate) || {
      vehicleId: plate,
      vehicleName: item.vehicleName || item.label || "",
      plateNumber: plate,
      marketPrice: 0,
      spent: 0,
      saved: 0,
    };
    current.marketPrice += dnMarketPrice(item);
    current.spent += dnSpentAmount(item);
    current.saved += dnSavedAmount(item);
    grouped.set(plate, current);
  });

  return [...grouped.values()]
    .map((item) => ({
      ...item,
      savingsPercent: savingsPercent(item.saved, item.marketPrice),
    }))
    .sort((a, b) => b.saved - a.saved);
};

const from2025Range = () => {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  return {
    from: new Date(2025, 0, 1).toISOString(),
    to: now.toISOString(),
  };
};

export async function loadSavingsQuestion(questionId) {
  switch (questionId) {
    case "totalSavings": {
      const rows = await fetchSavingsNotes(from2025Range());
      return { summary: summarizeSavings(rows) };
    }

    case "marketPriceVsSpent": {
      const rows = await fetchSavingsNotes(from2025Range());
      return {
        summary: summarizeSavings(rows),
        list: rows
          .map(mapSavingsRow)
          .sort((a, b) => b.marketPrice - a.marketPrice),
      };
    }

    case "savingsThisMonth": {
      const current = monthRange(0);
      const rows = await fetchSavingsNotes(current);
      const summary = summarizeSavings(rows);
      return {
        monthly: {
          month: current.label,
          marketPrice: summary.totalMarketPrice,
          spent: summary.totalSpent,
          saved: summary.totalSaved,
          savingsPercent: summary.savingsPercent,
          items: rows.map(mapSavingsRow),
          ...summary,
        },
      };
    }

    case "savingsByVehicle": {
      const rows = await fetchSavingsNotes(from2025Range());
      return {
        list: groupSavingsByVehicle(rows),
        summary: summarizeSavings(rows),
      };
    }

    default:
      return null;
  }
}
